<script setup lang="ts">
import VInfoBox from '@shared/component/VInfoBox/VInfoBox.vue';
import { computed, watch } from 'vue';
import { useBreadcrumbStore } from '@menu-lib/breadcrumb/breadcrumbStore';
import { getSurenetUrl } from '@menu-lib/urlHelper';
import { useRoute } from 'vue-router';
import { createErrorMessage } from '@errors/helpers';
import AdviseurMenu from '@crm-lib/adviseurs/adviseurbeheer/menu/AdviseurMenu.vue';
import adviseurbeheerRoutes from '@crm-lib/routes/adviseurbeheer.routes';
import FadeSlideTransition from '@shared/animation/transitions/FadeSlideTransition/FadeSlideTransition.vue';
import { useRouteAdviseurnummer } from '../composables/routeAdviseurnummer';
import { useAdviseur } from '../composables/adviseur';

const currentRoute = useRoute();

const tabName = computed(
  () =>
    `${currentRoute?.meta?.label}${currentRoute?.params?.polisnummer ? ` ${currentRoute?.params?.polisnummer}` : ''}`,
);

const { adviseurnummer } = useRouteAdviseurnummer();

const { isPending: adviseurPending, data: adviseur, error, isError } = useAdviseur();

const breadcrumbStore = useBreadcrumbStore();

const breadcrumbs = computed(() => {
  const url = '/app/crm/adviseurs';
  const base = { name: 'Adviseurbeheer', link: getSurenetUrl(url, false) };
  const adviseurPart = {
    name: `${adviseur.value?.naam || ''} (#${adviseurnummer.value})`,
    link: '',
  };
  const tab = { name: tabName.value || '', link: '' };

  if (adviseurPending.value || !adviseur.value?.naam) return [base];
  if (!tabName.value) return [base, adviseurPart];
  return [base, adviseurPart, tab] as Array<{ name: string; link: string }>;
});

watch(breadcrumbs, (b) => (breadcrumbStore.breadcrumbs = b));

const filteredRoutes = computed(() => {
  const type = adviseur.value?.type;
  return adviseurbeheerRoutes.filter((route) => {
    if (!route.meta.toonVoorTypes) return true;
    return type !== undefined && route.meta.toonVoorTypes.includes(type);
  });
});
</script>

<template>
  <div v-if="Number.isNaN(adviseurnummer) || isError" class="p-4">
    <VInfoBox badge-text="error" type="danger">
      <div v-if="Number.isNaN(adviseurnummer)">Het opgegeven adviseurnummer is ongeldig.</div>
      <div
        v-if="isError"
        v-sanitized-html="
          createErrorMessage(error, { onNotFoundError: `De opgegeven adviseur bestaat niet.` })
        "
      ></div>
    </VInfoBox>
  </div>
  <div class="flex h-full flex-col" v-else-if="!adviseurPending">
    <div class="relative flex grow flex-col xl:flex-row">
      <AdviseurMenu :routes="filteredRoutes" />
      <RouterView v-slot="{ Component }">
        <FadeSlideTransition direction="down" mode="out-in">
          <KeepAlive>
            <component :is="Component" />
          </KeepAlive>
        </FadeSlideTransition>
      </RouterView>
    </div>
  </div>
</template>
